/**
 * usePartnerData Hook
 * 
 * Clean hook for partner configurations list data management.
 * Handles fetching, filtering, pagination - components just consume.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks/redux';
import type { PartnerQueryParams } from '@/app/types/partners';
import {
  fetchPartners,
  setFilters,
  setPage,
  setPageSize,
  clearFilters,
  invalidate as invalidateAction,
  selectPartners,
  selectPartnersTotalCount,
  selectPartnersCurrentPage,
  selectPartnersPageSize,
  selectPartnersFilters,
  selectPartnersIsLoading,
  selectPartnersError,
  selectPartnersTotalPages,
  selectPartnersInvalidated,
  PAGE_SIZE_OPTIONS,
  type PageSizeOption,
} from '@/app/store/partnersSlice';

export function usePartnerData() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const prevInvalidatedRef = useRef(false);
  
  // Select all state
  const partners = useAppSelector(selectPartners);
  const totalCount = useAppSelector(selectPartnersTotalCount);
  const currentPage = useAppSelector(selectPartnersCurrentPage);
  const pageSize = useAppSelector(selectPartnersPageSize);
  const filters = useAppSelector(selectPartnersFilters);
  const isLoading = useAppSelector(selectPartnersIsLoading);
  const error = useAppSelector(selectPartnersError);
  const totalPages = useAppSelector(selectPartnersTotalPages);
  const invalidated = useAppSelector(selectPartnersInvalidated);

  // Initial fetch on mount or when invalidated
  useEffect(() => {
    if ((partners.length === 0 || invalidated) && !isLoading && !error) {
      dispatch(fetchPartners());
    }
  }, [invalidated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when invalidated changes from false to true (not just on mount)
  useEffect(() => {
    if (invalidated && !prevInvalidatedRef.current && !isLoading && !error) {
      dispatch(fetchPartners());
    }
    prevInvalidatedRef.current = invalidated;
  }, [invalidated, isLoading, error, dispatch]);

  // Refetch when returning to list page route if invalidated
  useEffect(() => {
    if (location.pathname === '/partners' && invalidated && !isLoading && !error) {
      dispatch(fetchPartners());
    }
  }, [location.pathname, invalidated, isLoading, error, dispatch]);

  // Focus/visibility handling - refetch when page becomes visible if invalidated
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && invalidated && !isLoading && !error) {
        dispatch(fetchPartners());
      }
    };

    const handleFocus = () => {
      if (invalidated && !isLoading && !error) {
        dispatch(fetchPartners());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [invalidated, isLoading, error, dispatch]);

  // Apply filters and fetch
  const applyFilters = useCallback((newFilters: Partial<PartnerQueryParams>) => {
    dispatch(setFilters(newFilters));
    dispatch(fetchPartners({ ...filters, ...newFilters, offset: 0 }));
  }, [dispatch, filters]);

  // Change page
  const goToPage = useCallback((page: number) => {
    dispatch(setPage(page));
    dispatch(fetchPartners({ ...filters, offset: (page - 1) * pageSize }));
  }, [dispatch, filters, pageSize]);

  // Refresh current view
  const refresh = useCallback(() => {
    dispatch(fetchPartners());
  }, [dispatch]);

  // Clear all filters
  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
    dispatch(fetchPartners());
  }, [dispatch]);

  // Mark list as invalidated (will refetch on next mount)
  const invalidate = useCallback(() => {
    dispatch(invalidateAction());
  }, [dispatch]);

  // Change page size
  const changePageSize = useCallback((size: number) => {
    // Validate size is one of the allowed options
    if (PAGE_SIZE_OPTIONS.includes(size as PageSizeOption)) {
      dispatch(setPageSize(size as PageSizeOption));
      dispatch(fetchPartners({ ...filters, limit: size, offset: 0 }));
    }
  }, [dispatch, filters]);

  return {
    // Data
    partners,
    totalCount,
    
    // Pagination
    currentPage,
    pageSize,
    totalPages,
    
    // Filters
    filters,
    
    // Status
    isLoading,
    error,
    
    // Actions
    applyFilters,
    goToPage,
    changePageSize,
    refresh,
    resetFilters,
    invalidate,
    
    // Constants
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}

